import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { authService } from '../services/auth/authService';

// Role hierarchy — higher index = more permissions
const ROLE_HIERARCHY: Record<string, number> = {
  'staff':                 1,
  'hr_manager':            2,
  'finance_manager':       2,
  'consultant_staff':      2,
  'admin':                 3,
  'hr_consultant':         3,
  'hr_consultant_admin':   3,
  'consultant_admin':      3,
  'superadmin':            4,
};

// What each permission requires as minimum role level
const PERMISSION_ROLE_LEVEL: Record<string, number> = {
  'read':                  1, // all roles
  'write':                 2, // hr_manager and above
  'delete':                3, // admin and above
  'manage':                3, // admin and above
  'manage_users':          3,
  'manage_billing':        3,
  'manage_settings':       3,
  'view_payroll':          2,
  'manage_payroll':        3,
  'view_all_employees':    2,
  'view_own_data':         1,
  'manage_recruitment':    2,
  'manage_documents':      2,
  'view_analytics':        2,
  'manage_benefits':       2,
  'manage_leave':          2,
  'approve_leave':         2,
  'superadmin_only':       4,
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (adminEmail: string, adminPassword: string) => Promise<void>;
  employeeLogin: (firstName: string, lastName: string, employeeIdOrEmail: string, organizationId: string) => Promise<void>;
  logout: () => void;
  checkPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isHRManager: () => boolean;
  isEmployee: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser]         = useState<User | null>(null);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    if (authService.isAuthenticated()) {
      setUser(authService.getCurrentUser());
    }
    setLoading(false);
  }, []);

  const login = async (adminEmail: string, adminPassword: string) => {
    setLoading(true);
    try {
      const response: AuthResponse = await authService.login(adminEmail, adminPassword);
      setUser(response.user);
      // Store role-based redirect target
      const role = response.user?.role || 'staff';
      const target = role === 'staff' ? '/my-dashboard' : '/dashboard';
      localStorage.setItem('hr_post_login_redirect', target);
    } finally {
      setLoading(false);
    }
  };

  const employeeLogin = async (
    firstName: string, lastName: string,
    employeeIdOrEmail: string, organizationId: string
  ) => {
    setLoading(true);
    try {
      const isEmail = employeeIdOrEmail.includes('@');
      const response: AuthResponse = isEmail
        ? await authService.employeeLogin('', '', employeeIdOrEmail, '')
        : await authService.employeeLogin(firstName, lastName, employeeIdOrEmail, organizationId);
      setUser(response.user);
      const role = response.user?.role || 'staff';
      const target = role === 'staff' ? '/my-dashboard' : '/dashboard';
      localStorage.setItem('hr_post_login_redirect', target);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Role-based permission check
  const checkPermission = (permission: string): boolean => {
    if (!user) return false;
    const userLevel     = ROLE_HIERARCHY[user.role] ?? 1;
    const requiredLevel = PERMISSION_ROLE_LEVEL[permission] ?? 4;
    // Flat permission check fallback
    if (user.permissions?.includes(permission)) return true;
    return userLevel >= requiredLevel;
  };

  const hasRole    = (role: string): boolean => user?.role === role;
  const isAdmin    = (): boolean => (ROLE_HIERARCHY[user?.role ?? ''] ?? 0) >= 3;
  const isHRManager = (): boolean => (ROLE_HIERARCHY[user?.role ?? ''] ?? 0) >= 2;
  const isEmployee  = (): boolean => user?.role === 'staff';

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, isLoading,
      login, employeeLogin, logout,
      checkPermission, hasRole, isAdmin, isHRManager, isEmployee
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
